import * as yup from "yup";
import { FilterDefinition } from "components/Shared/FilterEditor/utils";
import {
  getBooleanFilterDefinition,
  getDateTimeFilterDefinition,
  getSearchFilterDefinition,
  getStringFilterDefinition,
  getStringSelectFilterDefinition,
  SelectFilterDefinitionOptions,
} from "components/Shared/FilterEditor/standardFilters";
import BrandSelect from "components/Shared/Select/BrandSelect";
import { BrandFilter } from "__generated__/gql/graphql";
import createNestedFilterDefinitions from "components/Shared/FilterEditor/createNestedFilterDefinitions";
import { makeEnsureType } from "utils/typescriptUtilis";
import { assertIsString } from "utils/assertions";
import { ReactElement } from "react";

const filterObj = makeEnsureType<BrandFilter>();

type BrandSelectParams = Parameters<
  SelectFilterDefinitionOptions["renderSelectInput"]
>;

type Params = {
  renderBrandSelect: (
    formProps: BrandSelectParams[0],
    variantProps: BrandSelectParams[1] & { filter?: BrandFilter | undefined },
  ) => ReactElement;
};

export default function getFilterDefinitions({
  renderBrandSelect = ({ field }, { isMulti }) => (
    <BrandSelect
      {...field}
      isMulti={isMulti}
      menuPortalTarget={document.body}
    />
  ),
}: Params): FilterDefinition[] {
  const baseFilterDefinitions: FilterDefinition[] = [
    getSearchFilterDefinition({
      getResult: (search) => {
        assertIsString(search);
        return filterObj({
          or: [
            { name: { includesInsensitive: search } },
            { slug: { includesInsensitive: search } },
            { website: { includesInsensitive: search } },
          ],
        });
      },
    }),
    getStringSelectFilterDefinition({
      id: "brand",
      label: "Brand",
      getResult: (value, { filterVariantDefinition: { id: variantId } }) =>
        filterObj({ name: { [variantId]: value } }),
      getSelectResult: (
        value,
        { filterVariantDefinition: { id: variantId } },
      ) => filterObj({ id: { [variantId]: value } }),
      selectSchema: yup.string().uuid(),
      renderSelectInput: renderBrandSelect,
    }),
    getStringFilterDefinition({
      id: "slug",
      label: "Slug",
      getResult: (value, { filterVariantDefinition: { id: variantId } }) =>
        filterObj({ slug: { [variantId]: value } }),
    }),
    getStringFilterDefinition({
      id: "website",
      label: "Website",
      getResult: (value, { filterVariantDefinition: { id: variantId } }) =>
        filterObj({ website: { [variantId]: value } }),
    }),
    getBooleanFilterDefinition({ id: "isParent", label: "Parent?" }),
    getBooleanFilterDefinition({
      id: "isActive",
      label: "Active?",
      getResult: (value, { filterVariantDefinition: { id: variantId } }) =>
        filterObj({ isSubscriptionValid: { [variantId]: value } }),
    }),
    getDateTimeFilterDefinition({
      id: "createdAt",
      label: "Created Date",
      defaultVariant: "greaterThan",
    }),
  ];

  const parentFilterDefinitions = createNestedFilterDefinitions(
    "parent",
    baseFilterDefinitions.filter(
      (fd) => !["brand", "isParent"].includes(fd.id),
    ),
    { groupLabel: "Parent Brand" },
  );

  const filterDefinitions: FilterDefinition[] = [
    ...baseFilterDefinitions,
    getStringSelectFilterDefinition({
      id: "parent",
      label: "Brand",
      groupLabel: "Parent Brand",
      getResult: (value, { filterVariantDefinition: { id: variantId } }) =>
        filterObj({ parent: { name: { [variantId]: value } } }),
      getSelectResult: (
        value,
        { filterVariantDefinition: { id: variantId } },
      ) => filterObj({ parentId: { [variantId]: value } }),
      selectSchema: yup.string().uuid(),
      renderSelectInput: (formProps, variantProps) =>
        renderBrandSelect(formProps, {
          ...variantProps,
          filter: { isParent: { equalTo: true }, childBrandsExist: true },
        }),
    }),
    ...parentFilterDefinitions,
  ];

  return filterDefinitions;
}
